import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { useCart } from '../context/CartContext';
import { Plus, Minus, Check, Flame, Package, Play } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { VideoCardCompact } from './VideoCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductModal = ({ product, isOpen, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const [relatedVideos, setRelatedVideos] = useState([]);
  const { addToCart } = useCart();

  useEffect(() => {
    if (product && isOpen) {
      fetchRelatedVideos();
    }
  }, [product, isOpen]);

  const fetchRelatedVideos = async () => {
    try {
      const response = await axios.get(`${API}/videos/product/${product.id}`);
      setRelatedVideos(response.data.slice(0, 3)); // Max 3 videos
    } catch (error) {
      console.error('Failed to fetch related videos:', error);
    }
  };

  if (!product) return null;

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`${product.name} added to cart!`, {
      description: `Quantity: ${quantity}`,
    });
    setQuantity(1);
    onClose();
  };

  const colorMap = {
    'herb-savory': '#2D5016',
    'citrus-garlic': '#D4540A',
    'smoky-savory': '#5C3D2E',
    'seafood-seasoning': '#1E4D6B',
    'full-set': '#2A2A2A',
  };

  const accentColor = colorMap[product.id] || '#D4540A';
  const isBundle = product.is_bundle || product.id === 'full-set';

  // Bundle component products for display
  const bundleProducts = [
    { name: 'Herb & Savory', color: '#2D5016' },
    { name: 'Citrus Garlic', color: '#D4540A' },
    { name: 'Smoky Savory', color: '#5C3D2E' },
    { name: 'Seafood Seasoning', color: '#1E4D6B' }
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl bg-[#FAF8F5] max-h-[90vh] overflow-y-auto p-0" data-testid="product-modal" aria-describedby="product-description">
        <div className="grid md:grid-cols-2">
          {/* Product Image - Bundle shows official 4-bottle image */}
          <div className="p-6 md:p-8 flex items-center justify-center min-h-[350px] md:min-h-[450px]">
            {isBundle ? (
              <div className="w-full max-w-[400px] aspect-square rounded-xl overflow-hidden shadow-lg bg-[#3D2B1F] p-4">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-contain"
                  data-testid="modal-product-image"
                />
              </div>
            ) : (
              <div className="w-full max-w-[320px] aspect-square rounded-xl overflow-hidden shadow-lg">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="modal-product-image"
                />
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="p-6 md:p-8 space-y-5">
            <DialogHeader className="space-y-1 p-0">
              <p className="text-sm font-semibold uppercase tracking-wider" style={{ color: accentColor }}>
                {product.subtitle}
              </p>
              <DialogTitle className="font-['Bodoni_Moda'] text-3xl sm:text-4xl text-[#2A2A2A]">
                {product.name}
              </DialogTitle>
            </DialogHeader>

            <p className="text-base text-[#555]" data-testid="product-hook" id="product-description">
              {product.hook}
            </p>

            {/* Bundle value callout */}
            {isBundle && (
              <div className="bg-gradient-to-r from-[#2A2A2A] to-[#3a3a3a] rounded-lg p-4 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="w-5 h-5" />
                  <span className="font-bold">Complete Collection</span>
                </div>
                <p className="text-sm text-gray-300">
                  Save $2 vs buying separately! Get all 4 signature blends.
                </p>
              </div>
            )}

            <div className="text-3xl font-bold text-[#2A2A2A]" data-testid="product-price">
              ${product.price.toFixed(2)}
              {isBundle && (
                <span className="text-sm text-[#888] font-normal ml-2 line-through">
                  $31.96
                </span>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center space-x-4">
              <span className="text-sm font-medium text-[#666]">Quantity:</span>
              <div className="flex items-center border border-[#E5E0D8] rounded-lg bg-white">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="p-3 hover:bg-[#F5F3F0] transition-colors rounded-l-lg"
                  data-testid="quantity-decrease"
                >
                  <Minus className="w-4 h-4" />
                </button>
                <span className="px-5 font-semibold text-lg" data-testid="quantity-value">{quantity}</span>
                <button
                  onClick={() => setQuantity(quantity + 1)}
                  className="p-3 hover:bg-[#F5F3F0] transition-colors rounded-r-lg"
                  data-testid="quantity-increase"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Add to Cart Button */}
            <Button
              onClick={handleAddToCart}
              className="w-full py-6 text-lg font-semibold rounded-xl"
              style={{ backgroundColor: accentColor }}
              data-testid="add-to-cart-modal"
            >
              Add to Cart — ${(product.price * quantity).toFixed(2)}
            </Button>

            {/* Bundle: Show included products */}
            {isBundle ? (
              <div>
                <h4 className="font-semibold text-xs uppercase tracking-wider text-[#888] mb-3">
                  What's Included
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {bundleProducts.map((item, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white border border-[#E5E0D8]"
                    >
                      <div 
                        className="w-3 h-3 rounded-full" 
                        style={{ backgroundColor: item.color }}
                      />
                      <span className="text-sm font-medium text-[#2A2A2A]">{item.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              /* Flavor Profile Pills - for non-bundle */
              <div>
                <h4 className="font-semibold text-xs uppercase tracking-wider text-[#888] mb-2">
                  Flavor Profile
                </h4>
                <div className="flex flex-wrap gap-2">
                  {product.flavor_profile.map((flavor, idx) => (
                    <span
                      key={idx}
                      className="px-3 py-1.5 rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: accentColor }}
                    >
                      {flavor}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Perfect On - Food Visual Strip (only for non-bundle products) */}
        {!isBundle && product.perfect_on && product.perfect_on.length > 0 && (
          <div className="border-t border-[#E5E0D8] px-6 md:px-8 py-6 bg-white">
            <div className="flex items-center gap-2 mb-4">
              <Flame className="w-5 h-5" style={{ color: accentColor }} />
              <h4 className="font-semibold text-sm uppercase tracking-wider text-[#2A2A2A]">
                Perfect On
              </h4>
            </div>
            <div className="grid grid-cols-4 gap-3 md:gap-4">
              {product.perfect_on.map((item, idx) => (
                <div key={idx} className="text-center group">
                  <div className="aspect-square rounded-xl overflow-hidden mb-2 bg-[#F5F3F0] border border-[#E5E0D8] transition-all group-hover:shadow-md group-hover:border-transparent">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs font-medium text-[#555]">{item.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Usage Range & Best On - Compact */}
        <div className="grid sm:grid-cols-2 gap-4 px-6 md:px-8 pb-6 bg-[#FAF8F5]">
          {/* Usage Range */}
          <div className="bg-white rounded-xl p-4 border border-[#E5E0D8]">
            <h4 className="font-semibold text-xs uppercase tracking-wider text-[#888] mb-3">
              {isBundle ? 'Perfect For' : 'Usage Range'}
            </h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-[#2A2A2A] font-medium">{isBundle ? 'Gift Sets' : 'Light'}</span>
                <span className="text-[#666] text-right">{product.usage_range.light}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#2A2A2A] font-medium">{isBundle ? 'Exploring' : 'Medium'}</span>
                <span className="text-[#666] text-right">{product.usage_range.medium}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-[#2A2A2A] font-medium">{isBundle ? 'Stocking Up' : 'Heavy'}</span>
                <span className="text-[#666] text-right">{product.usage_range.heavy}</span>
              </div>
            </div>
          </div>

          {/* Great For */}
          <div className="bg-white rounded-xl p-4 border border-[#E5E0D8]">
            <h4 className="font-semibold text-xs uppercase tracking-wider text-[#888] mb-3">
              Great For
            </h4>
            <div className="flex flex-wrap gap-2">
              {product.great_for.map((item, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-[#F5F3F0] text-[#2A2A2A]"
                >
                  <Check className="w-3 h-3 mr-1" style={{ color: accentColor }} />
                  {item}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Related Videos Section */}
        {relatedVideos.length > 0 && (
          <div className="px-6 md:px-8 pb-6 bg-[#FAF8F5]">
            <div className="bg-white rounded-xl p-4 border border-[#E5E0D8]">
              <div className="flex items-center gap-2 mb-3">
                <Play className="w-4 h-4" style={{ color: accentColor }} fill={accentColor} />
                <h4 className="font-semibold text-xs uppercase tracking-wider text-[#888]">
                  Watch & Cook
                </h4>
              </div>
              <div className="space-y-2">
                {relatedVideos.map((video) => (
                  <VideoCardCompact key={video.id} video={video} />
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
